import React, { useEffect, useState } from 'react';
import proptypes from 'prop-types';
import { useLazyDataModel } from '@thd-nucleus/data-sources';
import { TextField, Typography } from '@one-thd/sui-atomic-components';
import {
  DELIVERY_ZIP_HELPER_TEXT,
  DELIVERY_ZIP_REQUIRED_MESSAGE,
  INVALID_ZIP_CODE
} from '../constants';
import { isZipRejectedByLookup } from '../helpers';

const { bool, func, string } = proptypes;

/**
 * The required Delivery Zip Code at the top of the Create Project drawer. [DSCVR-5168 AC1]
 *
 * It reads and writes the same jobsiteInformation.zipCode as the zip inside Address Details, so
 * the two fields are the same value by construction rather than being kept in step by an effect
 * - which is what AC8 asks for. Only the ids differ, so both can be in the DOM at once.
 *
 * Unlike the Address Details zip - optional until an address line is entered - this one is
 * always required, so it carries its own error channel into the drawer's submit guard.
 */
export const DeliveryZipField = ({
  zipCode,
  setJobsiteInformation,
  showErrors,
  setIsDeliveryZipError
}) => {
  const [error, setError] = useState('');

  // A well-formed zip that doesn't exist (e.g. 11111) passes the format check, so only the
  // city/state lookup can reject it. This is the same cache-first query the Address Details zip
  // runs, so it is answered once; reading it here puts the error on the field the pro typed in,
  // instead of only on the copy inside the accordion, which is usually collapsed.
  const [fetchCityState, cityAndStateResponse] = useLazyDataModel('cityStateLookupByZipCode', {
    variables: {},
    skip: (zipCode?.length !== 5),
    context: { withAuth: true },
    fetchPolicy: 'cache-first'
  });

  useEffect(() => {
    if (zipCode?.length === 5) {
      fetchCityState({ variables: { zipCode } });
    }
  }, [zipCode]);

  const isZipNotFound = isZipRejectedByLookup(zipCode, cityAndStateResponse);

  const validate = (value) => {
    if (!value) {
      return DELIVERY_ZIP_REQUIRED_MESSAGE;
    }
    if (!/^\d{5}$/.test(value) || isZipNotFound) {
      return INVALID_ZIP_CODE;
    }
    return '';
  };

  useEffect(() => {
    const message = validate(zipCode);
    setError(message);
    setIsDeliveryZipError(!!message);
  }, [zipCode, isZipNotFound, setIsDeliveryZipError]);

  const handleOnChange = (event) => {
    const { value } = event.target;
    setJobsiteInformation((prevState) => ({
      ...prevState,
      zipCode: value
    }));
  };

  return (
    <div>
      <TextField
        fullWidth
        required
        value={zipCode || ''}
        id="delivery-zipcode"
        label="Delivery Zip Code"
        name="deliveryZipCode"
        onChange={handleOnChange}
        status={showErrors && error ? 'error' : null}
        statusMessage={showErrors ? error : ''}
        InputProps={{
          inputProps: {
            'data-testid': 'delivery-zipcode-input',
            maxLength: '5'
          }
        }}
      />
      <div className="sui-mt-1">
        <Typography variant="body-base" color="subtle">
          {DELIVERY_ZIP_HELPER_TEXT}
        </Typography>
      </div>
    </div>
  );
};

DeliveryZipField.displayName = 'DeliveryZipField';

DeliveryZipField.propTypes = {
  zipCode: string,
  setJobsiteInformation: func.isRequired,
  showErrors: bool,
  setIsDeliveryZipError: func.isRequired
};

DeliveryZipField.defaultProps = {
  zipCode: '',
  showErrors: false
};
